import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, Hourglass } from "lucide-react";

const POLL_TIMEOUT_MS = 60_000;
const POLL_INTERVAL_MS = 2_000;
const PAID_PLANS = new Set(["starter", "pro"]);

function hasPaidSuccess(me) {
  if (!me || typeof me !== "object") return false;
  const plan = String(me.plan || me.plan_tier || "").toLowerCase();
  const credits = Number(me.credits || 0);
  const status = String(me.subscription_status || "").toLowerCase();
  if (!PAID_PLANS.has(plan)) return false;
  if (credits <= 0) return false;
  if (status && !["active", "trialing"].includes(status)) return false;
  return true;
}

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const sid = params.get("session_id");
  const [phase, setPhase] = useState(sid ? "polling" : "missing_sid");
  const [me, setMe] = useState(null);
  const cancelledRef = useRef(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get(`/auth/me`);
      return data || null;
    } catch {
      return null;
    }
  }, []);

  const startPolling = useCallback(async () => {
    cancelledRef.current = false;
    const start = Date.now();
    while (!cancelledRef.current) {
      const data = await fetchMe();
      if (data && hasPaidSuccess(data)) {
        setMe(data);
        setPhase("success");
        try { await refresh(); } catch { /* ignore refresh errors */ }
        return;
      }
      if (Date.now() - start >= POLL_TIMEOUT_MS) {
        setMe(data);
        setPhase("pending");
        return;
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  }, [fetchMe, refresh]);

  useEffect(() => {
    if (!sid) return;
    startPolling();
    return () => { cancelledRef.current = true; };
  }, [sid, startPolling]);

  const refreshNow = useCallback(async () => {
    setPhase("polling");
    startPolling();
  }, [startPolling]);

  const displayPlan = useMemo(() => {
    const p = String(me?.plan || me?.plan_tier || "").toLowerCase();
    if (p === "starter") return "Starter";
    if (p === "pro") return "Pro";
    return "";
  }, [me]);

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      {phase === "polling" && (
        <div data-testid="billing-polling">
          <Loader2 className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-6" />
          <h1 className="text-2xl font-semibold mb-2">{t("billing_success.polling_title")}</h1>
          <p className="text-zinc-500">{t("billing_success.polling_sub")}</p>
        </div>
      )}

      {phase === "success" && (
        <div data-testid="billing-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-6" />
          <h1 className="text-3xl font-semibold tracking-tight mb-2">{t("billing_success.success_title")}</h1>
          <p className="text-zinc-400 mb-2">
            {t("billing_success.success_plan_prefix")}{" "}
            <span className="text-white font-medium">{displayPlan}</span>.
          </p>
          <p className="text-zinc-400 mb-6">
            {t("billing_success.success_credits_prefix")}{" "}
            <span className="font-mono text-white" data-testid="success-credits">
              {Number(me?.credits || 0)}
            </span>
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/generate" data-testid="success-generate-btn">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white">{t("billing_success.success_generate")}</Button>
            </Link>
            <Link to="/account" data-testid="success-account-btn">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("billing_success.success_account")}
              </Button>
            </Link>
            <Link to="/dashboard" data-testid="success-dashboard-btn">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("billing_success.success_dashboard")}
              </Button>
            </Link>
          </div>
        </div>
      )}

      {phase === "pending" && (
        <div data-testid="billing-pending">
          <Hourglass className="w-14 h-14 mx-auto text-zinc-400 mb-6" />
          <h1 className="text-2xl font-semibold mb-2">{t("billing_success.pending_title")}</h1>
          <p className="text-zinc-500 mb-6">
            {t("billing_success.pending_sub")}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              onClick={refreshNow}
              data-testid="billing-refresh-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white"
            >
              {t("billing_success.refresh_status")}
            </Button>
            <Link to="/account" data-testid="billing-account-btn">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("billing_success.success_account")}
              </Button>
            </Link>
            <Link to="/pricing" data-testid="billing-back-pricing-btn">
              <Button variant="outline" className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white">
                {t("billing_success.back_to_pricing")}
              </Button>
            </Link>
          </div>
        </div>
      )}

      {phase === "missing_sid" && (
        <div data-testid="billing-missing-sid">
          <Hourglass className="w-14 h-14 mx-auto text-zinc-400 mb-6" />
          <h1 className="text-2xl font-semibold mb-2">{t("billing_success.missing_session_title")}</h1>
          <p className="text-zinc-500 mb-6">
            {t("billing_success.missing_session_sub")}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/account">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white">{t("billing_success.success_account")}</Button>
            </Link>
            <Button
              variant="outline"
              onClick={() => nav("/pricing")}
              className="border-[#1E212B] bg-[#12141C] hover:bg-[#161821] text-white"
            >
              {t("billing_success.back_to_pricing")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
